/**-----------------------------------------------------------------------------
 *                                    E2RH
 * -----------------------------------------------------------------------------
 *
 *  filename: migration.tsx
 *
 *  author: tcleret
 *
 *  creation: 16/12/2025 - 11:43
 *
 *  description:
 *
 *  Copyright (c) 2025 E2RH
 * -----------------------------------------------------------------------------
 */

import * as React from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import * as utils from "../utils/utils.tsx";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Collapse from "@mui/material/Collapse";
import Button from "@mui/material/Button";
import Done from "@mui/icons-material/Done";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Checkbox from "@mui/material/Checkbox";
import Cancel from "@mui/icons-material/Cancel";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ReplayIcon from "@mui/icons-material/Replay";
import UnfoldMoreDoubleIcon from "@mui/icons-material/UnfoldMoreDouble";

/**
 * Display the input parametes UI for the migration
 * @returns The Parameters selection JSX object
 */
function SelectMigrationParams({ onSetNextState, onSetMigrationParams }) {
   const [opDomains, setOpDomains] = React.useState([]);
   const [silaeDomains, setSilaeDomains] = React.useState([]);
   const [silaeFolders, setSilaeFolders] = React.useState([]);
   const [selectedOpDomain, setSelectedOpDomain] = React.useState("");
   const [selectedSilaeDomain, setSelectedSilaeDomain] = React.useState("");
   const [moisReprise, setMoisReprise] = React.useState("");
   const moisRepriseDefaultValue = "Non spécifié";

   // Table display
   const [selectedFolders, setSelectedFolders] = React.useState([]);

   let stepValid = false;

   // Create callbacks to set the caller's state
   const setNextState = React.useCallback(
      (event) => {
         onSetNextState(event);
      },
      [onSetNextState]
   );
   const setMigrationParams = React.useCallback(
      (event) => {
         onSetMigrationParams(event);
      },
      [onSetMigrationParams]
   );

   // Get the domains list for Openpaye
   async function getOpDomains() {
      const reply = await utils.apiClient.post("/migrapaye/getOpDomains");

      const _domainsSelect = [];
      let i = 0;
      for (const line of reply.data.stdout) {
         if (line.startsWith("| - ")) {
            const domain = line.split(" - ")[1].split(" ")[0];
            _domainsSelect.push(
               <MenuItem key={i} value={domain} selected={i === 0}>
                  {domain}
               </MenuItem>
            );
            i++;
         }
      }
      setOpDomains(_domainsSelect);
      const value = localStorage.getItem("migration_opDomain");
      if (value != null) {
         handleOpDomainChange(value);
      }
   }

   // Get the domains list for Silae
   async function getSilaeDomains() {
      try {
         const reply = await utils.apiClient.post("/e2rhApi/getDomains");
         if (reply != null) {
            const _domainsSelect = [];
            let i = 0;
            for (const domain of reply.data.Data) {
               _domainsSelect.push(
                  <MenuItem key={i} value={domain} selected={i === 0}>
                     {domain}
                  </MenuItem>
               );
               i++;
            }
            setSilaeDomains(_domainsSelect);
            const value = localStorage.getItem("migration_silaeDomain");
            const folders = localStorage.getItem("migration_silaeFolders");
            if (value != null) {
               setSelectedSilaeDomain(value);
               getSilaeFolders(value);
            }
            if (folders != null) {
               setSelectedFolders(JSON.parse(folders));
            }
         } else {
            toast.error("Erreur lors de la récupération des domaines Silae : pas de réponse de E2RH-API");
         }
      } catch (error) {
         toast.error("Erreur lors de la récupération des domaines Silae : " + error);
      }
   }

   // Get the domains list for Silae
   async function getSilaeFolders(value) {
      const reply = await utils.apiClient.post("/e2rhApi/getFolders", {
         domain: value,
      });
      if (reply != null) {
         setSilaeFolders(reply.data.Data);
      } else {
         setSilaeFolders([]);
         setSelectedFolders([]);
         toast.error("Erreur lors de la récupération des dossiers Silae : pas de réponse de E2RH-API");
      }
   }

   function handleSilaeDomainChange(value) {
      setSelectedFolders([]);
      setSelectedSilaeDomain(value);
      getSilaeFolders(value);
      localStorage.setItem("migration_silaeDomain", value);
      localStorage.removeItem("migration_silaeFolders");
   }

   function handleOpDomainChange(value) {
      setSelectedOpDomain(value);
      localStorage.setItem("migration_opDomain", value);
   }

   function handleMoisReprise(value) {
      setMoisReprise(value);
      localStorage.setItem("migration_moisReprise", value);
   }

   function handleValidate() {
      setMigrationParams({
         inputDomain: selectedSilaeDomain,
         outputDomain: selectedOpDomain,
         moisReprise: moisReprise,
         folders: selectedFolders.map((id) => {
            const folder = silaeFolders.find((folder) => folder.numero === id);
            return { numero: folder.numero, raisonSociale: folder.raisonSociale, siret: folder.siret };
         }),
      });
      setNextState();
   }

   const handleSelectAllClick = (event) => {
      if (event.target.checked) {
         const newSelected = silaeFolders.map((n) => n.numero);
         setSelectedFolders(newSelected);
         localStorage.setItem("migration_silaeFolders", JSON.stringify(newSelected));
      } else {
         setSelectedFolders([]);
         localStorage.removeItem("migration_silaeFolders");
      }
   };

   function handleSelectClick(event, id) {
      const selectedIndex = selectedFolders.indexOf(id);
      let newSelected = [];

      if (selectedIndex === -1) {
         newSelected = newSelected.concat(selectedFolders, id);
      } else if (selectedIndex === 0) {
         newSelected = newSelected.concat(selectedFolders.slice(1));
      } else if (selectedIndex === selectedFolders.length - 1) {
         newSelected = newSelected.concat(selectedFolders.slice(0, -1));
      } else if (selectedIndex > 0) {
         newSelected = newSelected.concat(
            selectedFolders.slice(0, selectedIndex),
            selectedFolders.slice(selectedIndex + 1)
         );
      }
      setSelectedFolders(newSelected);
      localStorage.setItem("migration_silaeFolders", JSON.stringify(newSelected));
   }

   // Populate dropdowns
   if (opDomains.length < 1 && silaeDomains.length < 1) {
      getOpDomains();
      getSilaeDomains();
   }

   // Populate mois reprise menu
   const moisRepriseMenu = [];
   for (let i = 0; i <= 12; i++) {
      if (i === 0) {
         moisRepriseMenu.push(
            <MenuItem key={i} value={i} selected={i === 0}>
               {moisRepriseDefaultValue}
            </MenuItem>
         );
      } else {
         moisRepriseMenu.push(
            <MenuItem key={i} value={i} selected={false}>
               {i}
            </MenuItem>
         );
      }
   }

   // Initialize the MoisReprise menu's value
   if (moisReprise === "") {
      const value = localStorage.getItem("migration_moisReprise");
      if (value != null) {
         setMoisReprise(value);
      } else {
         setMoisReprise(moisRepriseDefaultValue);
      }
   }

   // Write step validity
   if (selectedOpDomain !== "" && selectedSilaeDomain !== "" && selectedFolders.length > 0) {
      stepValid = true;
   }

   return (
      <div>
         <div style={{ marginTop: "40px", display: "flex", flexDirection: "row", gap: "20px" }}>
            <div style={{ width: "35%" }}>
               <InputLabel id="demo-simple-select-label"> Domaine source Silae </InputLabel>
               <Select
                  className="w100"
                  value={selectedSilaeDomain}
                  onChange={(event) => handleSilaeDomainChange(event.target.value)}
                  label="Domaine source Silae"
               >
                  {silaeDomains}
               </Select>
            </div>
            <div style={{ width: "35%" }}>
               <InputLabel> Domaine cible Openpaye </InputLabel>
               <Select
                  className="w100"
                  value={selectedOpDomain}
                  onChange={(event) => handleOpDomainChange(event.target.value)}
                  label="Domaine cible Openpaye"
               >
                  {opDomains}
               </Select>
            </div>
            <div style={{ width: "30%" }}>
               <InputLabel> Mois de reprise </InputLabel>
               <Select
                  className="w100"
                  value={moisReprise}
                  onChange={(event) => handleMoisReprise(event.target.value)}
                  label="Mois de reprise"
               >
                  {moisRepriseMenu}
               </Select>
            </div>
         </div>
         <InputLabel style={{ marginTop: "40px" }}> Dossiers source Silae </InputLabel>
         <TableContainer component={Paper} sx={{ marginTop: "20px", height: "calc(100vh - 540px)", width: "100%" }}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table" stickyHeader size="small">
               <TableHead>
                  <TableRow>
                     <TableCell>
                        <Checkbox
                           color="primary"
                           indeterminate={selectedFolders.length > 0 && selectedFolders.length < silaeFolders.length}
                           checked={silaeFolders.length > 0 && selectedFolders.length === silaeFolders.length}
                           onChange={handleSelectAllClick}
                           inputProps={{
                              "aria-label": "select all",
                           }}
                           style={{ padding: 0 }}
                        />
                     </TableCell>
                     <TableCell>ID</TableCell>
                     <TableCell>Raison Sociale</TableCell>
                     <TableCell>SIRET</TableCell>
                  </TableRow>
               </TableHead>
               <TableBody>
                  {silaeFolders.map((row, index) => {
                     const isItemSelected = selectedFolders.includes(row.numero);
                     return (
                        <TableRow
                           hover
                           onClick={(event) => handleSelectClick(event, row.numero)}
                           role="checkbox"
                           aria-checked={isItemSelected}
                           tabIndex={-1}
                           key={index}
                           selected={isItemSelected}
                           sx={{ cursor: "pointer" }}
                        >
                           <TableCell selected={isItemSelected}>
                              <Checkbox color="primary" checked={isItemSelected} style={{ padding: 0 }} />
                           </TableCell>
                           <TableCell component="th" scope="row">
                              {row.numero}
                           </TableCell>
                           <TableCell>{row.raisonSociale}</TableCell>
                           <TableCell>{row.siret}</TableCell>
                        </TableRow>
                     );
                  })}
               </TableBody>
            </Table>
         </TableContainer>
         <div
            style={{
               display: "flex",
               flexDirection: "row",
               justifyContent: "flex-end",
               gap: "10px",
               marginTop: "20px",
            }}
         >
            <Button
               variant="contained"
               disabled={!stepValid}
               className={stepValid ? "table-button-add" : "table-button-disabled"}
               size="large"
               startIcon={<Done />}
               onClick={handleValidate}
            >
               Valider
            </Button>
         </div>
      </div>
   );
}

SelectMigrationParams.propTypes = {
   onSetNextState: PropTypes.func,
   onSetMigrationParams: PropTypes.func,
};

/**
 * Display the interface to get information on what is going to be migrated
 * @returns The Migration preview JSX object
 */
function MigrationPreview({ onSetNextState, onSetPreviousState, migrationParams }) {
   const [open, setOpen] = React.useState(migrationParams.folders.map(() => false));
   const [infoData, setInfoData] = React.useState(migrationParams.folders.map(() => null));
   const nof_columns = 4;

   // Create callbacks to set the caller's state
   const setNextState = React.useCallback(
      (event) => {
         onSetNextState(event);
      },
      [onSetNextState]
   );
   const setPreviousState = React.useCallback(
      (event) => {
         onSetPreviousState(event);
      },
      [onSetPreviousState]
   );

   /**
    * Handler for expanding/collapsing folder information
    *
    * Keep the data in memory (thus using an array) to avoid multiple calls to the
    * backend (some can take several seconds for the big folders like Gibert)
    */
   function handleSelectClick(event, id, index) {
      if (!open[index]) {
         if (infoData[index] == null) {
            setInfoData(infoData.map((val, idx) => (idx === index ? null : val)));
            const reply = utils.apiClient.post("/e2rhApi/getFolderInfo", {
               domain: migrationParams.inputDomain,
               folder: id,
            });
            reply.then((res) => {
               setInfoData(infoData.map((val, idx) => (idx === index ? res.data : val)));
            });
         }
      }
      setOpen(open.map((val, idx) => (idx === index ? !val : false)));
   }

   /**
    * Display the information about the folder in the collapsible area
    *
    * @param index The index of the infoData to display
    * @returns The JSX object
    */
   function displayInfoData(index) {
      if (infoData[index] != null) {
         return (
            <Box sx={{ margin: 1 }}>
               <div style={{ display: "flex", flexDirection: "row" }}>
                  <div style={{ display: "flex", flexDirection: "column", width: "50%" }}>
                     <h4> Données présentes sur Silae :</h4>
                     <div key={0}> Nombre d&apos;employés : {infoData[index].empls} </div>
                     <div key={1}> Nombre d&apos;établissements : {infoData[index].etabs.length} </div>
                     {infoData[index].etabs.map((etab, idx) => (
                        <div key={idx + 2} style={{ marginTop: "10px" }}>
                           <b>
                              Établissement {etab.nom} {etab.principal ? " - Principal" : ""} :
                           </b>
                           <div> SIRET : {etab.siret} </div>
                           <div> Adresse : {etab.adresse}</div>
                        </div>
                     ))}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", width: "50%" }}>
                     <h4> Données présentes sur OpenPaye :</h4>
                     <div key={0}> Nombre d&apos;employés : pas lu </div>
                     <div key={1}> Nombre d&apos;établissements : pas lu </div>
                  </div>
               </div>
            </Box>
         );
      } else {
         return (
            <Box sx={{ margin: 1 }}>
               <div>Récupération des informations depuis Silae ...</div>
            </Box>
         );
      }
   }

   function getMonthDisplayString(mois) {
      const string_prefix = "sur le mois d";
      let value = "";
      if (mois == 0 || mois === "Non spécifié") {
         value = ", mois de reprise non spécifié";
      } else if (mois == 1) {
         value = string_prefix + "e Janvier";
      } else if (mois == 2) {
         value = string_prefix + "e Février";
      } else if (mois == 3) {
         value = string_prefix + "e Mars";
      } else if (mois == 4) {
         value = string_prefix + "'Avril";
      } else if (mois == 5) {
         value = string_prefix + "e Mai";
      } else if (mois == 6) {
         value = string_prefix + "e Juin";
      } else if (mois == 7) {
         value = string_prefix + "e Juillet";
      } else if (mois == 8) {
         value = string_prefix + "'Août";
      } else if (mois == 9) {
         value = string_prefix + "e Septembre";
      } else if (mois == 10) {
         value = string_prefix + "'Octobre";
      } else if (mois == 11) {
         value = string_prefix + "e Novembre";
      } else if (mois == 12) {
         value = string_prefix + "e Décembre";
      }
      return value;
   }

   return (
      <div>
         <h3>
            Migration depuis le domaine Silae &apos;{migrationParams.inputDomain}&apos; vers le domaine Openpaye &apos;
            {migrationParams.outputDomain}&apos; {getMonthDisplayString(migrationParams.moisReprise)}
         </h3>
         <Box sx={{ marginTop: "20px", marginBottom: "30px", width: "100%" }}>
            <InputLabel style={{ marginTop: "40px" }}> Dossiers sélectionnés pour la migration : </InputLabel>
            <TableContainer component={Paper} sx={{ marginTop: "20px", height: "calc(100vh - 470px)", width: "100%" }}>
               <Table sx={{ minWidth: 650 }} aria-label="simple table" stickyHeader size="small">
                  <TableHead>
                     <TableRow>
                        <TableCell />
                        <TableCell>ID</TableCell>
                        <TableCell>Raison Sociale</TableCell>
                        <TableCell>SIRET</TableCell>
                     </TableRow>
                  </TableHead>
                  <TableBody>
                     {migrationParams.folders.map((row, index) => {
                        return [
                           <TableRow
                              hover
                              onClick={(event) => handleSelectClick(event, row.numero, index)}
                              key={index}
                              sx={{ cursor: "pointer", "& > *": { borderBottom: "unset" } }}
                           >
                              <TableCell component="th" scope="row">
                                 <IconButton aria-label="expand row" size="small">
                                    {open[index] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                 </IconButton>
                              </TableCell>
                              <TableCell>{row.numero}</TableCell>
                              <TableCell>{row.raisonSociale}</TableCell>
                              <TableCell>{row.siret}</TableCell>
                           </TableRow>,
                           open[index] ? (
                              <TableRow key={index + 0.5}>
                                 <TableCell style={{ paddingBottom: "10px", paddingTop: 0 }} colSpan={nof_columns}>
                                    <Collapse in={open[index]} timeout="auto" unmountOnExit>
                                       {displayInfoData(index)}
                                    </Collapse>
                                 </TableCell>
                              </TableRow>
                           ) : null,
                        ];
                     })}
                  </TableBody>
               </Table>
            </TableContainer>
         </Box>
         <div
            style={{
               display: "flex",
               flexDirection: "row",
               justifyContent: "flex-end",
               gap: "10px",
               marginTop: "20px",
            }}
         >
            <Button
               style={{ width: "300px" }}
               variant="contained"
               className="table-button-edit"
               size="large"
               startIcon={<SkipPreviousIcon />}
               onClick={setPreviousState}
            >
               Modifier les paramètres
            </Button>
            <Button
               style={{ width: "300px" }}
               variant="contained"
               className="table-button-add"
               size="large"
               startIcon={<Done />}
               onClick={setNextState}
            >
               Lancer la migration
            </Button>
         </div>
      </div>
   );
}

MigrationPreview.propTypes = {
   onSetNextState: PropTypes.func,
   onSetPreviousState: PropTypes.func,
   migrationParams: PropTypes.object,
};

/**
 * Display the output of the migration operation
 * @returns The Migration output JSX object
 */
function MigrationOutput({ onSetNextState, onSetPreviousState, migrationParams }) {
   const [migrateScriptOutput, setMigrateScriptOutput] = React.useState("");
   const [startScript, setStartScript] = React.useState(false);
   const [isRunning, setIsRunning] = React.useState(true);
   const [activateScroll, setActivateScroll] = React.useState(true);
   const activateScrollRef = React.useRef(activateScroll);
   const maxStdoutSize = 1000;
   React.useEffect(() => {
      activateScrollRef.current = activateScroll;
   }, [activateScroll]);

   // Create callbacks to set the caller's state
   // Create callbacks to set the caller's state
   const setNextState = React.useCallback(
      (event) => {
         onSetNextState(event);
      },
      [onSetNextState]
   );
   const setPreviousState = React.useCallback(
      (event) => {
         onSetPreviousState(event);
      },
      [onSetPreviousState]
   );

   async function delay(ms: number) {
      return new Promise((resolve) => setTimeout(resolve, ms));
   }

   // Run the migration script
   async function runMigrationScript() {
      // Send the requests: firt to launch the script, then in a loop to get the status until script is done
      try {
         let reply = await utils.apiClient.post("/migrapaye/migrate", {
            domain_in: migrationParams.inputDomain,
            domain_out: migrationParams.outputDomain,
            reprise: migrationParams.moisReprise,
            folders: migrationParams.folders.map((folder) => folder.numero),
         });
         const script_id = reply.data.script_id;
         let isRunning = true;
         let script_output = "";
         const script_output_array = [];

         // String display while running
         while (isRunning) {
            await delay(400);
            reply = await utils.apiClient.post("/migrapaye/getStatus", { script_id: script_id });

            isRunning = reply.data.isRunning;

            // Fill the limited-size buffer to display stdout history
            for (const line of reply.data.stdout) {
               script_output_array.push(line);
            }
            for (const line of reply.data.stderr) {
               script_output_array.push("\\[91m" + line + "\\[0m");
            }
            if (script_output_array.length > maxStdoutSize) {
               script_output_array.splice(0, script_output_array.length - maxStdoutSize);
            }
            script_output = "";
            for (const line of script_output_array) {
               script_output += "\n" + line;
            }
            //  #TODO: remove when getStatus is working
            // isRunning = i < 50;
            // stdout += "Simulation de la \\[91msortie du script\\[0m de migration \\[94m(loop " + i++ + ")\\[0m...\n";
            setMigrateScriptOutput(
               <div
                  className="migrapaye_raw_result"
                  id="scrollable_text"
                  dangerouslySetInnerHTML={{ __html: utils.formatString(script_output) }}
               ></div>
            );
            await delay(100);
            if (activateScrollRef.current) {
               const text = document.getElementById("scrollable_text");
               if (text) {
                  text.scrollTop = text.scrollHeight;
               }
            }
         }
      } catch (error) {
         toast.error("Erreur lors de la migration : " + error);
      }
      setIsRunning(false);
   }

   if (!startScript) {
      setStartScript(true);
      runMigrationScript();
   }

   return (
      <div>
         <h3> Suivi de la migration </h3>
         <div style={{ height: "calc(100vh - 450px)" }}>{migrateScriptOutput}</div>
         <div
            style={{
               display: "flex",
               flexDirection: "row",
               justifyContent: "flex-end",
               gap: "10px",
               marginTop: "20px",
            }}
         >
            {/* <Button
               style={{ width: "300px" }}
               disabled={!isRunning}
               variant="contained"
               className={isRunning ? "table-button-delete" : "table-button-disabled"}
               size="large"
               startIcon={<Cancel />}
               onClick={setPreviousState}
            >
               Arrêter la migration
            </Button> */}
            <Button
               style={{ width: "300px" }}
               variant="contained"
               className="table-button-edit"
               size="large"
               startIcon={<UnfoldMoreDoubleIcon />}
               onClick={() => setActivateScroll(!activateScroll)}
            >
               {activateScroll ? "Désactiver" : "Activer"} le défilement auto
            </Button>
            <Button
               style={{ width: "300px" }}
               disabled={isRunning}
               variant="contained"
               className={isRunning ? "table-button-disabled" : "table-button-add"}
               size="large"
               startIcon={<ReplayIcon />}
               onClick={setNextState}
            >
               Nouvelle migration
            </Button>
         </div>
      </div>
   );
}

MigrationOutput.propTypes = {
   onSetNextState: PropTypes.func,
   onSetPreviousState: PropTypes.func,
   migrationParams: PropTypes.object,
};

/**
 * Interface for the migration operation
 */
export default function Migration() {
   const [activeStep, setActiveStep] = React.useState(0);
   const [migrationParams, setMigrationParams] = React.useState({ inputDomain: "", folders: [], outputDomain: "" });
   const steps = [
      "Selection des paramètres de migration",
      "Visualisation des données à migrer",
      "Suivi de la Migration",
   ];

   // Get the step page content
   let step_content = null;
   if (activeStep === 0) {
      step_content = <SelectMigrationParams onSetNextState={handleNext} onSetMigrationParams={setMigrationParams} />;
   } else if (activeStep === 1) {
      step_content = (
         <MigrationPreview
            onSetNextState={handleNext}
            onSetPreviousState={handlePrevious}
            migrationParams={migrationParams}
         />
      );
   } else if (activeStep === 2) {
      step_content = (
         <MigrationOutput
            onSetNextState={handleNext}
            onSetPreviousState={handlePrevious}
            migrationParams={migrationParams}
         />
      );
   }

   // Go to next/previous step
   function handleNext() {
      if (activeStep < steps.length - 1) {
         setActiveStep(activeStep + 1);
      } else {
         setActiveStep(0);
      }
   }
   function handlePrevious() {
      if (activeStep > 0) {
         setActiveStep(activeStep - 1);
      }
   }

   return (
      <div className="action_content">
         <h3> Migration </h3>
         <Box sx={{ marginTop: "20px", marginBottom: "30px", width: "100%" }}>
            <Stepper activeStep={activeStep} alternativeLabel>
               {steps.map((label, index) => (
                  <Step key={index}>
                     <StepLabel>{label}</StepLabel>
                  </Step>
               ))}
            </Stepper>
         </Box>
         {step_content}
      </div>
   );
}
